import CashTamerHttpClient from './CashTamerHttpClient';
import CashTamerApiBase from './CashTamerApiBase';
import MemberBankAccountResponseModel from '@/models/member/MemberBankAccountResponseModel';
import MemberCommunicationResponseModel from '@/models/member/MemberCommunicationResponseModel';
import MemberLogResponseModel from '@/models/member/MemberLogResponseModel';
import MemberOrderResponseModel from '@/models/member/MemberOrderResponseModel';
import MemberProfileResponseModel from '@/models/member/MemberProfileResponseModel';
import MemberChangeBackupPasswordRequestModel from '@/models/member/MemberChangeBackupPasswordRequestModel';
import MemberChangeEmailAddressRequestModel from '@/models/member/MemberChangeEmailAddressRequestModel';
import MemberChangeNameRequestModel from '@/models/member/MemberChangeNameRequestModel';
import MemberChangePrimaryPasswordRequestModel from '@/models/member/MemberChangePrimaryPasswordRequestModel';
import MemberConfirmBankAccountRequestModel from '@/models/member/MemberConfirmBankAccountRequestModel';
import MemberInitiateEmailAddressChangeRequestModel from '@/models/member/MemberInitiateEmailAddressChangeRequestModel';
import MemberPurchaseBankAccountsRequestModel from '@/models/member/MemberPurchaseBankAccountsRequestModel';
import MemberRefreshTokenResponseModel from '@/models/member/MemberRefreshTokenResponseModel';

export default class MemberApi extends CashTamerApiBase {

    public async changeBackupPasswordAsync(jwt: string, request: MemberChangeBackupPasswordRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/backuppassword';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changeEmailAddressAsync(jwt: string, request: MemberChangeEmailAddressRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/emailaddress';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changeNameAsync(jwt: string, request: MemberChangeNameRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/name';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changePrimaryPasswordAsync(jwt: string, request: MemberChangePrimaryPasswordRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/primarypassword';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async confirmBankAccountMembershipAsync(jwt: string, request: MemberConfirmBankAccountRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/confirmbankaccountmembership';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async deleteAsync(jwt: string): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member';
            await httpClient.secureDeleteAsync(url, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async getCommunicationsAsync(jwt: string): Promise<MemberCommunicationResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const httpResponse = await httpClient.secureGetAsync('member/communications', jwt);

            const values = new Array<MemberCommunicationResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: MemberCommunicationResponseModel = new MemberCommunicationResponseModel();
                value.communicationDateTimeUtc = httpResponse.data[i].communicationDateTimeUtc;
                value.communicationType = httpResponse.data[i].communicationType;
                value.communicationToken = httpResponse.data[i].communicationToken;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getBankAccountsAsync(jwt: string): Promise<MemberBankAccountResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const httpResponse = await httpClient.secureGetAsync('member/bankaccounts', jwt);

            const values = new Array<MemberBankAccountResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: MemberBankAccountResponseModel = new MemberBankAccountResponseModel();
                value.bankAccountId = httpResponse.data[i].bankAccountId;
                value.bankAccountName = httpResponse.data[i].bankAccountName;
                value.balance = httpResponse.data[i].balance;
                value.confirmed = httpResponse.data[i].confirmed;
                value.createdBy = httpResponse.data[i].createdBy;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getLogsAsync(jwt: string): Promise<MemberLogResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const httpResponse = await httpClient.secureGetAsync('member/logs', jwt);

            const values = new Array<MemberLogResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: MemberLogResponseModel = new MemberLogResponseModel();
                value.fieldName = httpResponse.data[i].fieldName;
                value.oldValue = httpResponse.data[i].oldValue;
                value.newValue = httpResponse.data[i].newValue;
                value.createdDateTimeUtc = httpResponse.data[i].createdDateTimeUtc;
                value.createdBy = httpResponse.data[i].createdBy;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getOrdersAsync(jwt: string): Promise<MemberOrderResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const httpResponse = await httpClient.secureGetAsync('member/orders', jwt);

            const values = new Array<MemberOrderResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: MemberOrderResponseModel = new MemberOrderResponseModel();
                value.orderId = httpResponse.data[i].orderId;
                value.productName = httpResponse.data[i].productName;
                value.quantity = httpResponse.data[i].quantity;
                value.price = httpResponse.data[i].price;
                value.extendedPrice = httpResponse.data[i].extendedPrice;
                value.discount = httpResponse.data[i].discount;
                value.subTotal = httpResponse.data[i].subTotal;
                value.tax = httpResponse.data[i].tax;
                value.total = httpResponse.data[i].total;
                value.referenceNumber = httpResponse.data[i].referenceNumber;
                value.createdDateTimeUtc = httpResponse.data[i].createdDateTimeUtc;
                value.createdBy = httpResponse.data[i].createdBy;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getProfileAsync(jwt: string) {

        try {
            const httpClient = new CashTamerHttpClient();

            const httpResponse = await httpClient.secureGetAsync('member', jwt);

            const profile = new MemberProfileResponseModel();
            profile.createdDateTimeUtc = httpResponse.data.createdDateTimeUtc;
            profile.currentSignInDateTimeUtc = httpResponse.data.currentSignInDateTimeUtc;
            profile.emailAddress = httpResponse.data.emailAddress;
            profile.givenName = httpResponse.data.givenName;
            profile.lastBackupPasswordChangeDateTimeUtc = httpResponse.data.lastBackupPasswordChangeDateTimeUtc;
            profile.lastPrimaryPasswordChangeDateTimeUtc = httpResponse.data.lastPrimaryPasswordChangeDateTimeUtc;
            profile.lastSignInDateTimeUtc = httpResponse.data.lastSignInDateTimeUtc;
            profile.surname = httpResponse.data.surname;

            return profile;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async initiateEmailAddressChangeAsync(jwt: string, request: MemberInitiateEmailAddressChangeRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/emailaddress/initiatechange';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async purchaseBankAccountsAsync(jwt: string, request: MemberPurchaseBankAccountsRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/purchase';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async refreshTokenAsync(jwt: string): Promise<MemberRefreshTokenResponseModel> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/refreshtoken';
            const httpResponse = await httpClient.securePostAsync(url, null, jwt);

            const response = new MemberRefreshTokenResponseModel();
            response.jwt = httpResponse.data.jwt;
            response.jwtExpirationMs = httpResponse.data.jwtExpirationMs;

            return response;
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

}
